import { useCallback, useState } from 'react';
import { LIBS, LIBS_PROVIDERS, PROVIDERS } from './providers';
import { encodeBase64 } from '@libs/shared/helpers/Encoding';
import { useAppSelector } from '../services/hooks.service';
import { useActiveAccount } from './useActiveAccount';
import { secureStorage } from '../services/localStorage.service';
import algosdk from 'algosdk';
import { generateLoadingToast, getUpdatedToastOptions, isKeyExpired } from '../_utils';
import { PeraConnector } from './algorand/pera.service';
import { SolanaConnector } from './solana/solana.service';
import { EVMConnector } from './evm/evm.service';
import { toast } from 'react-toastify';

const solanaConnector = SolanaConnector.getInstance();
const evmConnector = EVMConnector.getInstance();
const peraConnector = PeraConnector.getInstance();

export const useSignMessage = () => {
  const provider = useAppSelector(state => state.network.provider);
  const [message, setMessage] = useState<string | null>(null);
  const activeWallet = useActiveAccount();

  //sign methods abstraction
  const signWithWeb3Modal = async (message: string, encoding: BufferEncoding | undefined) => {
    if (evmConnector.isConnected && evmConnector.address) {
      const signature = await evmConnector.signMessage(message, encoding);
      
      return signature;
    }
  };

  const signWithPera = async (message: string, encoding: BufferEncoding | undefined): Promise<string> => {
    if (provider === PROVIDERS.PERA) {
      const signature = await peraConnector.signMessage(message, encoding);
      
      return signature;
    }
  };

  const signWithSolana = async (message: string, encoding: BufferEncoding | undefined) => {
    const signature = await solanaConnector.signMessage(message, encoding);
    
    return signature;
  }
  
  function findLibraryByProvider(provider) {
    for (const [lib, providers] of Object.entries(LIBS_PROVIDERS)) {
      if (providers.includes(provider)) {
        return lib;
      }
    }
  }

  const signMethods: {[key:string]: (data: string, encoding: BufferEncoding | undefined) => Promise<string>} = {
    [LIBS.USEWALLET]: signWithPera,
    [LIBS.WEB3MODAL]: signWithWeb3Modal,
    [LIBS.SOLANA]: signWithSolana,
  };

  const signMessage = useCallback<(data: string, encoding?: BufferEncoding) => Promise<string>>(async (data: string, encoding?: BufferEncoding) => {
    try {
      const signMethod = signMethods[findLibraryByProvider(provider)];
      if (!signMethod) {
        throw new Error(`Signing method not found for provider: ${provider}`);
      }
      const res = await signMethod(data, encoding);
      setMessage(res);
      return res;
    } catch (error) {
      console.error('Error signing the message:', error);
      throw error;
    }
  }, [activeWallet?.address, provider, signMethods]);

  const signMessageByToken = useCallback<(data: string, encoding?: BufferEncoding) => Promise<string>>(async (data: string, encoding?: BufferEncoding) => {
    try {
      const keyData = secureStorage.getKeyFromLocalStorage();
      const isExpired = isKeyExpired(keyData?.expiredAt);

      if (!keyData || isExpired) {
        const res = await signMessage(data, encoding);
        return res;
      } else {
        const message = typeof data === "string" ? data : JSON.stringify(data);
        const bytes = new Uint8Array(Buffer.from(message, encoding));
        const { sk } = algosdk.mnemonicToSecretKey(keyData.mnemonic);
        const res = algosdk.signBytes(bytes, sk);
        const signature = encodeBase64(res);
        setMessage(signature);
        return signature;
      }
    } catch (error) {
      console.error('Error signing the message:', error);
      throw error;
    }
  }, [activeWallet?.address, signMessage]);

  return {
    signMessage,
    signMessageByToken,
    message,
  };
};
